'use client';

import * as React from 'react';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';

export function InjectCustomChatMessages() {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();

  React.useEffect(() => {
    if (typeof window === 'undefined' || !localParticipant) return;

    const localParticipantIdentity = localParticipant.identity;

    const injectStyles = () => {
      if (document.querySelector('#custom-chat-styles')) {
        return;
      }

      const style = document.createElement('style');
      style.id = 'custom-chat-styles';
      style.textContent = `
        .lk-chat-entry[data-lk-message-origin="local"] {
          justify-content: flex-end !important;
          flex-direction: column !important;
          align-items: flex-end !important;
        }

        .lk-chat-entry[data-lk-message-origin="local"] .lk-meta-data {
          text-align: right !important;
          margin-bottom: 4px !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .lk-chat-entry[data-lk-message-origin="local"] .lk-message-body {
          background: #3b82f6 !important;
          color: white !important;
          border-radius: 18px 18px 4px 18px !important;
          max-width: 70% !important;
          padding: 12px 16px !important;
          margin-left: auto !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
          display: inline-block !important;
        }

        .lk-chat-entry:not([data-lk-message-origin="local"]) {
          justify-content: flex-start !important;
          flex-direction: column !important;
          align-items: flex-start !important;
        }

        .lk-chat-entry:not([data-lk-message-origin="local"]) .lk-meta-data {
          text-align: left !important;
          margin-bottom: 4px !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .lk-chat-entry:not([data-lk-message-origin="local"]) .lk-message-body {
          background: var(--lk-bg2, #374151) !important;
          color: var(--lk-fg, #ffffff) !important;
          border-radius: 18px 18px 18px 4px !important;
          max-width: 70% !important;
          padding: 12px 16px !important;
          margin-right: auto !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          display: inline-block !important;
        }

        @media (max-width: 640px) {
          .lk-chat-entry .lk-message-body {
            max-width: 85% !important;
          }
        }
      `;

      document.head.appendChild(style);
    };

    const processMessages = () => {
      const messages = document.querySelectorAll('.lk-chat-entry');

      messages.forEach((messageEl, index) => {
        if (messageEl.hasAttribute('data-processed')) {
          return;
        }

        const isLocal = messageEl.getAttribute('data-lk-message-origin') === 'local';

        messageEl.setAttribute('data-processed', 'true');
      });
    };

    const checkChatLoaded = () => {
      const chatContainer = document.querySelector('.lk-chat');
      const messagesContainer = document.querySelector('.lk-chat-messages');
      const messages = document.querySelectorAll('.lk-chat-entry');

      return chatContainer && messagesContainer;
    };

    const setupChat = () => {
      injectStyles();

      setTimeout(processMessages, 100);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.classList?.contains('lk-chat-entry')) {
                  setTimeout(processMessages, 50);
                }
              }
            });
          }
        });
      });

      const messagesContainer = document.querySelector('.lk-chat-messages');
      if (messagesContainer) {
        observer.observe(messagesContainer, {
          childList: true,
          subtree: true,
        });
      }

      return observer;
    };

    let observer: MutationObserver | null = null;

    if (checkChatLoaded()) {
      observer = setupChat();
    }

    const interval = setInterval(() => {
      if (checkChatLoaded() && !observer) {
        observer = setupChat();
        clearInterval(interval);
      }
    }, 500);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      clearInterval(interval);
    };
  }, [localParticipant]);

  return null;
}
